/**
 * DataHealthDashboard - Modal showing API endpoint health status
 *
 * Displays real-time status of all API endpoints with:
 * - Success/error indicators
 * - Response times
 * - Data age (staleness)
 * - Validation status
 * - Last update timestamp
 */

import { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Grid,
  Box,
  Text,
  Flex,
  Badge,
  Circle,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  useColorMode
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase } from '@fortawesome/free-solid-svg-icons';

export const DataHealthDashboard = ({ isOpen, onClose, metadata }) => {
  const { colorMode } = useColorMode();
  const bgColor = colorMode === 'dark' ? '#121212' : '#ffffff';
  const borderColor = colorMode === 'dark' ? '#ffffff' : '#121212';
  const textColor = colorMode === 'dark' ? 'white' : 'black';
  const labelColor = colorMode === 'dark' ? 'gray.400' : 'gray.600';

  // Calculate summary stats
  const summary = useMemo(() => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return {
        total: 0,
        success: 0,
        error: 0,
        invalid: 0,
        avgResponseTime: 0
      };
    }

    const total = Object.keys(metadata).length;
    const success = Object.values(metadata).filter(m => m.status === 'success').length;
    const error = Object.values(metadata).filter(m => m.status === 'error').length;
    const invalid = Object.values(metadata).filter(m => m.status === 'invalid').length;

    const responseTimes = Object.values(metadata)
      .map(m => m.requestTime)
      .filter(t => t != null);

    const avgResponseTime =
      responseTimes.length > 0
        ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
        : 0;

    return {
      total,
      success,
      error,
      invalid,
      avgResponseTime,
      successRate: total > 0 ? Math.round((success / total) * 100) : 0
    };
  }, [metadata]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green.500';
      case 'error':
        return 'red.500';
      case 'invalid':
        return 'orange.500';
      default:
        return 'gray.500';
    }
  };

  // Get status badge
  const getStatusBadge = (meta) => {
    if (!meta) return <Badge colorScheme="gray">Unknown</Badge>;

    switch (meta.status) {
      case 'success':
        return <Badge colorScheme="green">Success</Badge>;
      case 'error':
        return <Badge colorScheme="red">Error</Badge>;
      case 'invalid':
        return <Badge colorScheme="orange">Invalid</Badge>;
      default:
        return <Badge colorScheme="gray">Unknown</Badge>;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Get age in seconds
  const getAge = (timestamp) => {
    if (!timestamp) return 'N/A';
    const age = Math.floor((Date.now() - timestamp) / 1000);
    if (age < 60) return `${age}s ago`;
    const minutes = Math.floor(age / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size={{ base: "full", md: "6xl" }}>
      <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
      <ModalContent
        bg={bgColor}
        pb={6}
        borderWidth="2px"
        borderStyle="solid"
        borderColor={borderColor}
        borderRadius="6px"
        overflow="hidden"
        maxH="95vh"
        color={textColor}
      >
        <ModalHeader color={textColor}>
          <FontAwesomeIcon icon={faDatabase} /> &nbsp;Endpoint Health
        </ModalHeader>
        <ModalCloseButton
          color={textColor}
          _hover={{
            bg: 'gray.500',
          }}
          _focus={{
            boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
          }}
        />
        <ModalBody pb={6}>
          {/* Summary Statistics */}
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)"
            }}
            gap={4}
            mb={6}
          >
            <Stat>
              <StatLabel color={labelColor} fontSize="sm">Total Endpoints</StatLabel>
              <StatNumber color={textColor} fontSize="3xl">{summary.total}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color={labelColor} fontSize="sm">Success</StatLabel>
              <StatNumber color="green.400" fontSize="3xl">{summary.success}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color={labelColor} fontSize="sm">Errors</StatLabel>
              <StatNumber color="red.400" fontSize="3xl">{summary.error}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color={labelColor} fontSize="sm">Success Rate</StatLabel>
              <StatNumber color={textColor} fontSize="3xl">{summary.successRate}%</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color={labelColor} fontSize="sm">Avg Response</StatLabel>
              <StatNumber color={textColor} fontSize="3xl">{summary.avgResponseTime}ms</StatNumber>
              <StatHelpText color={labelColor}>Last fetch</StatHelpText>
            </Stat>
          </Grid>

          <Divider borderColor={labelColor} mb={6} />

          {/* Individual Endpoint Status */}
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
              xl: "repeat(5, 1fr)",
              "2xl": "repeat(6, 1fr)"
            }}
            gap={4}
          >
            {metadata && Object.entries(metadata)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([endpoint, meta]) => (
                <Box
                  key={endpoint}
                  p={4}
                  bg={colorMode === 'dark' ? 'gray.800' : 'gray.50'}
                  borderWidth="1px"
                  borderColor={labelColor}
                  borderRadius="md"
                  _hover={{ borderColor: getStatusColor(meta.status) }}
                  transition="border-color 0.2s"
                >
                  <Flex align="center" justify="space-between" mb={2}>
                    <Flex align="center" gap={2}>
                      <Circle size="10px" bg={getStatusColor(meta.status)} />
                      <Text fontWeight="bold" fontSize="sm" color={textColor}>
                        TCS {endpoint}
                      </Text>
                    </Flex>
                    {getStatusBadge(meta)}
                  </Flex>

                  <Box fontSize="xs" color={labelColor}>
                    {meta.requestTime != null && (
                      <Text mb={1} color={labelColor}>Response: {meta.requestTime}ms</Text>
                    )}

                    {meta.timestamp && (
                      <Text mb={1} color={labelColor}>
                        Updated: {formatTimestamp(meta.timestamp)}
                      </Text>
                    )}

                    {meta.timestamp && (
                      <Text mb={1} color={getAge(meta.timestamp).includes('h') ? 'red.400' : labelColor}>
                        Age: {getAge(meta.timestamp)}
                      </Text>
                    )}

                    {meta.error && (
                      <Text color="red.400" mt={2} fontSize="10px">
                        Error: {meta.error}
                      </Text>
                    )}

                    {meta.warnings && meta.warnings.length > 0 && (
                      <Text color="orange.400" mt={2} fontSize="10px">
                        Warnings: {meta.warnings.length}
                      </Text>
                    )}
                  </Box>
                </Box>
              ))}
          </Grid>

          {(!metadata || Object.keys(metadata).length === 0) && (
            <Box textAlign="center" py={10}>
              <Text color={labelColor}>No endpoint data available yet</Text>
              <Text color={labelColor} fontSize="sm" mt={2} opacity={0.7}>
                Data will appear after the first API fetch
              </Text>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
